// File: TopSushiVancouverArticle.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function TopSushiVancouverArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "top-7-sushi-restaurants-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Miku Restaurant (High-End)",
    "section-2": "Sushi California (Budget-Friendly)",
    "section-3": "Toshi Sushi (Mid-Range)",
    "section-4": "Tojo’s Restaurant (High-End Omakase)",
    "section-5": "Minami (Mid-Range to High-End)",
    "section-6": "Sushi Bella (Budget to Mid-Range)",
    "section-7": "Sushi Kame (Mid-Range)",
    "section-8": "Vegetarian & Vegan Sushi Options",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Tourism Vancouver Official Website",
    },
    {
      url: "https://vancouver.ca/people-programs/food-safety.aspx",
      label: "City of Vancouver’s Food Safety Page",
    },
    {
      text: "Official disclaimers and guidelines from Vancouver Coastal Health."
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p> or <li> section. Coordinates go in `lat`/`lng`.
  const locations = [
    {
      id: "section-1",
      lat: 49.2860,
      lng: -123.1139,
      popupText: "Miku Restaurant (High-End)",
    },
    {
      id: "section-2",
      lat: 49.2485,
      lng: -123.0443,
      popupText: "Sushi California (Budget-Friendly)",
    },
    {
      id: "section-3",
      lat: 49.2618,
      lng: -123.1003,
      popupText: "Toshi Sushi (Mid-Range)",
    },
    {
      id: "section-4",
      lat: 49.2630,
      lng: -123.1428,
      popupText: "Tojo’s Restaurant (High-End Omakase)",
    },
    {
      id: "section-5",
      lat: 49.2746,
      lng: -123.1208,
      popupText: "Minami (Mid-Range to High-End)",
    },
    {
      id: "section-6",
      lat: 49.2810,
      lng: -123.1405,
      popupText: "Sushi Bella (Budget to Mid-Range)",
    },
    {
      id: "section-7",
      lat: 49.2633,
      lng: -123.1380,
      popupText: "Sushi Kame (Mid-Range)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Top 7 Sushi Restaurants in Vancouver for Every Budget</title>
        <meta
          name="description"
          content="A practical guide to Vancouver's top sushi spots for all budgets, including location details, approximate costs, and must-try dishes."
        />
        <meta
          name="keywords"
          content="Vancouver sushi, budget-friendly sushi, omakase, aburi sushi, Japanese cuisine"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/top-7-sushi-restaurants-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Top 7 Sushi Restaurants in Vancouver for Every Budget"
        />
        <meta
          property="og:description"
          content="Explore the best sushi Vancouver has to offer, with something for every budget."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/top-7-sushi-restaurants-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/top-7-sushi-restaurants-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Top 7 Sushi Restaurants in Vancouver for Every Budget"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver's top sushi spots for every price range."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/top-7-sushi-restaurants-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A scenic view of sushi dishes in Vancouver."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s rich multicultural scene has turned it into a sushi
              lover’s paradise. Whether you’re on a tight budget, looking for
              mid-range quality, or in search of a luxurious omakase experience,
              the city offers a vast array of options. This guide spotlights
              seven notable sushi restaurants that cater to all budgets,
              complete with practical tips such as addresses, hours, and
              highlights.
            </p>

            {/* SECTION: Miku Restaurant (High-End) */}
            <h2 id="section-1">Miku Restaurant (High-End)</h2>
            <p>
              <strong>Address:</strong> 70-200 Granville St, Vancouver, BC V6C
              1S4
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$40–CA\$60 (lunch), CA\$50–CA\$80
              (dinner)
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Fri: 11:30 AM – 10:00 PM;
              Sat–Sun: 5:00 PM – 10:00 PM
            </p>
            <p>
              Known for its mouthwatering aburi (flame-seared) sushi and
              breathtaking waterfront views, Miku is an ideal spot for special
              occasions. Try their signature Salmon Oshi Sushi for a flavorful
              twist on classic nigiri.
            </p>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2860}
                coordX={-123.1139}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (High-end sushi experience in Downtown Vancouver.)
            </p>

            {/* SECTION: Sushi California (Budget-Friendly) */}
            <h2 id="section-2">Sushi California (Budget-Friendly)</h2>
            <p>
              <strong>Address:</strong> 3883 Rupert St, Vancouver, BC V5R 2G7
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$15–CA\$25 per person
            </p>
            <p>
              <strong>Operating Hours:</strong> Daily: 11:00 AM – 10:00 PM
            </p>
            <p>
              Sushi California is a go-to option for those on a budget. You’ll
              find generous portions at wallet-friendly prices, perfect for
              quick lunches or casual dinners. Their creative rolls and bento
              boxes are popular among local students and families alike.
            </p>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2485}
                coordX={-123.0443}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Perfect for a quick, affordable sushi fix.)
            </p>

            {/* SECTION: Toshi Sushi (Mid-Range) */}
            <h2 id="section-3">Toshi Sushi (Mid-Range)</h2>
            <p>
              <strong>Address:</strong> 181 E 16th Ave, Vancouver, BC V5T 2T7
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$20–CA\$35 per person
            </p>
            <p>
              <strong>Operating Hours:</strong> Tue–Sun: 4:30 PM – 9:30 PM
              (closed Mon)
            </p>
            <p>
              Toshi Sushi is a beloved neighborhood spot known for its
              consistently fresh nigiri and sashimi. Its cozy interior offers a
              warm, friendly ambience that keeps locals coming back. Arrive
              early if you can—no reservations mean lines can form quickly.
            </p>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2618}
                coordX={-123.1003}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Popular local gem with a cozy setting.)
            </p>

            {/* SECTION: Tojo’s Restaurant (High-End Omakase) */}
            <h2 id="section-4">Tojo’s Restaurant (High-End Omakase)</h2>
            <p>
              <strong>Address:</strong> 1133 W Broadway, Vancouver, BC V6H 1G1
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$80–CA\$150+ per person for
              omakase
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Sat: 5:00 PM – 10:00 PM
              (closed Sun)
            </p>
            <p>
              Chef Hidekazu Tojo is famed for inventing the California Roll, and
              his namesake restaurant offers meticulously crafted omakase meals
              that highlight premium, seasonal ingredients. This is a
              must-visit for sushi enthusiasts seeking a refined, immersive
              dining experience.
            </p>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2630}
                coordX={-123.1428}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (A legendary chef’s gourmet omakase destination.)
            </p>

            {/* SECTION: Minami (Mid-Range to High-End) */}
            <h2 id="section-5">Minami (Mid-Range to High-End)</h2>
            <p>
              <strong>Address:</strong> 1118 Mainland St, Vancouver, BC V6B 2T9
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$30–CA\$50 (lunch); CA\$40–CA\$70
              (dinner)
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Sun: 11:30 AM – 3:00 PM,
              5:00 PM – 10:00 PM
            </p>
            <p>
              A sister restaurant to Miku, Minami in Yaletown also specializes
              in aburi sushi. Its chic and modern setting makes it a favorite
              for date nights and business lunches. Don’t miss their inventive
              signature rolls and sophisticated cocktail pairings.
            </p>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2746}
                coordX={-123.1208}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Upscale ambiance with flame-seared sushi specialties.)
            </p>

            {/* SECTION: Sushi Bella (Budget to Mid-Range) */}
            <h2 id="section-6">Sushi Bella (Budget to Mid-Range)</h2>
            <p>
              <strong>Address:</strong> 1935 Cornwall Ave, Vancouver, BC V6J 1C8
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$15–CA\$30 per person
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Sun: 11:00 AM – 10:00 PM
            </p>
            <p>
              Cozy and inviting, Sushi Bella is perfect for families or casual
              gatherings. They offer vegetarian-friendly rolls alongside classic
              nigiri and sushi combos, all at reasonable prices. The portion
              sizes are generous, making it easy to share dishes.
            </p>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2810}
                coordX={-123.1405}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Affordable spot near Kitsilano Beach.)
            </p>

            {/* SECTION: Sushi Kame (Mid-Range) */}
            <h2 id="section-7">Sushi Kame (Mid-Range)</h2>
            <p>
              <strong>Address:</strong> 3096 Cambie St, Vancouver, BC V5Z 2V9
            </p>
            <p>
              <strong>Approx. Cost:</strong> CA\$25–CA\$40 per person
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Sat: 12:00 PM – 9:30 PM
              (closed Sun)
            </p>
            <p>
              Sushi Kame prides itself on serving incredibly fresh daily
              specials, especially tuna that melts in your mouth. The staff are
              known for friendly service, guiding patrons toward the day’s
              must-try items. This unassuming spot is a hidden treasure among
              sushi enthusiasts.
            </p>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location Marker:</strong>{" "}
              <Marker
                coordY={49.2633}
                coordX={-123.1380}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Fresh daily specials and welcoming atmosphere.)
            </p>

            {/* SECTION: Vegetarian & Vegan Sushi Options */}
            <h2 id="section-8">Vegetarian & Vegan Sushi Options</h2>
            <p>
              Vancouver’s rising health-conscious culture means many sushi
              restaurants now feature dedicated vegetarian and vegan menus. Look
              for avocado, cucumber, and inari (tofu pouch) rolls, or ask about
              specialty plant-based nigiri. Spots like Sushi Bella often have
              robust veggie offerings at an affordable price point.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What’s the best way to avoid long waits at popular sushi spots?</h3>
              <p>
                Visit during off-peak hours (like mid-afternoon) or call ahead to
                see if you can reserve. Restaurants like Toshi Sushi can have
                long lines, so arrive early or be prepared to wait.
              </p>

              <h3>2. Are these restaurants suitable for large groups?</h3>
              <p>
                Many high-end spots like Miku or Tojo’s can accommodate larger
                parties, but booking in advance is highly recommended. Smaller,
                neighborhood eateries may have limited seating.
              </p>

              <h3>3. Do these places offer takeout or delivery?</h3>
              <p>
                Most of them do. Check each restaurant’s official website or
                local delivery platforms for their takeout or delivery options.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Liquor laws in British Columbia require guests to be 19+ years old
              to order alcohol, and you may be asked for valid ID. Some
              restaurants close between lunch and dinner service, so always
              confirm hours. Vancouver also enforces strict food safety
              inspections; all mentioned restaurants adhere to Vancouver Coastal
              Health guidelines.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From budget-friendly options like Sushi California to
              once-in-a-lifetime omakase at Tojo’s, Vancouver has a sushi spot
              for everyone. Whether you’re sharing bento boxes with friends or
              treating yourself to flame-seared delicacies, these seven places
              guarantee an unforgettable dining experience. Now that you know
              the best places to go, it’s time to indulge in some of the
              freshest sushi the city has to offer!
            </p>

            <p>
              <em>
                Disclaimer: Always check official resources for the latest hours
                and safety guidelines. Restaurant information may change, so
                confirm details before visiting.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}